<div class="flex h-full flex-col bg-white dark:bg-gray-900 w-full">
  <!-- Sticky Header with Running Total -->
  <div class="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 p-6">
    <div class="flex items-center justify-between gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Select £150 Offer Products</h2>
      </div>
    </div>
    @if (errorMessage) {
      <div class="mt-3 p-3 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-200">
        {{ errorMessage }}
      </div>
    }
    <div class="mt-3 space-y-2">
      <div class="flex justify-between text-sm">
        <span class="text-gray-600 dark:text-gray-400">Selected Total (existing + new):</span>
        <span class="font-medium text-gray-900 dark:text-white">£{{ selectedTotal | number:'1.2-2' }}</span>
      </div>
      <div class="flex justify-between text-sm">
        <span class="text-gray-600 dark:text-gray-400">Discount Used:</span>
        <span class="font-medium" [ngClass]="{
          'text-green-600 dark:text-green-400': selectedTotal <= maxOfferAmount,
          'text-amber-600 dark:text-amber-400': selectedTotal > maxOfferAmount
        }">
          £{{ (selectedTotal > maxOfferAmount ? maxOfferAmount : selectedTotal) | number:'1.2-2' }} of £{{ maxOfferAmount | number:'1.2-2' }}
        </span>
      </div>
      @if (selectedTotal > maxOfferAmount) {
        <div class="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded text-xs text-amber-800 dark:text-amber-200">
          <strong>Heads up:</strong> Anything above £{{ maxOfferAmount | number:'1.2-2' }} will be charged at full price.
        </div>
      }
    </div>
  </div>

  <div class="flex-1 overflow-y-auto p-6 space-y-8">
    <!-- New offer product form (reuses main add form with offer filter locked) -->
    <section class="border border-gray-200 dark:border-gray-800 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/40">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Add a new offer product</h3>
        <span class="text-xs text-gray-500 dark:text-gray-400">Filters locked to £150 offer items</span>
      </div>
      @if (isOverOfferCredit) {
        <div class="mb-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-200">
          The £{{ maxOfferAmount | number:'1.2-2' }} credit is fully allocated. You can still add more offer products — anything beyond £{{ maxOfferAmount | number:'1.2-2' }} is charged at full price.
        </div>
      }
      <app-event-product-item-form
        [event]="event"
        [filterPreset]="{ minSpend: false, qualifying: false, offer: true }"
        [filtersLocked]="true"
        [submitLabel]="'Add to offer basket'"
        (itemCreated)="stageNewOfferItem($event)"
      ></app-event-product-item-form>
    </section>

    <!-- Staged new items -->
    <section>
      @if (stagedNewItems.length) {
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">New items to add</h3>
        </div>
        <div class="space-y-2">
          @for (item of stagedNewItems; track item.id) {
            <div class="border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 flex justify-between items-start">
              <div>
                <div class="font-semibold text-gray-900 dark:text-white">{{ item.product.name }}</div>
                <div class="text-xs text-gray-500 dark:text-gray-300">Will be added and discounted when you apply.</div>
                @if (item.variant_selections?.length) {
                  <div class="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                    @for (selection of item.variant_selections; track selection.id) {
                      <div class="flex justify-between gap-2">
                        <span>{{ selection.variant?.name }}: {{ selection.variant_option?.name || 'Standard' }}</span>
                        <span>×{{ selection.quantity }}</span>
                      </div>
                    }
                  </div>
                } @else {
                  <div class="mt-1 text-xs text-gray-600 dark:text-gray-300">Qty: {{ item.quantity || '—' }}</div>
                }
                <div class="text-sm font-semibold text-gray-900 dark:text-white mt-1">£{{ item.calculated_price | number:'1.2-2' }}</div>
              </div>
              <button
                class="text-xs text-red-600 dark:text-red-300 hover:underline disabled:opacity-50"
                [disabled]="processing"
                (click)="removeStagedItem(item.id)">
                Remove
              </button>
            </div>
          }
        </div>
      }
    </section>

    <!-- Existing event items eligible for offer -->
    <section>
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Currently applied offer items</h3>
      </div>
      @if (appliedExistingItems.length === 0) {
        <div class="text-sm text-gray-500 dark:text-gray-400">No offer items are currently applied.</div>
      } @else {
        <div class="space-y-3">
          @for (item of appliedExistingItems; track item.id) {
            <div [ngClass]="{
              'product-card p-4 border rounded-lg transition-colors': true,
              'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800': !isSelected(item),
              'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/20': isSelected(item),
              'opacity-60 pointer-events-none': isBlocked(item) && !isSelected(item)
            }">
              <label class="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  [checked]="isSelected(item)"
                  (change)="toggleProduct(item)"
                  [disabled]="isCheckboxDisabled(item)"
                  class="mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <div class="ml-3 flex-1">
                  <div class="flex items-center gap-2 mb-1 flex-wrap">
                    <div class="font-medium text-gray-900 dark:text-white">{{ item.product.name }}</div>
                    <span class="px-2 py-0.5 text-[11px] font-semibold rounded bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 inline-flex items-center gap-1">
                      <lucide-icon [name]="GiftIcon" [size]="12" class="shrink-0"></lucide-icon>
                      £150 Offer
                    </span>
                    @if (item.product.counts_to_500_deal) {
                      <span class="px-2 py-0.5 text-[11px] font-semibold rounded bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 inline-flex items-center gap-1">
                        <lucide-icon [name]="StarIcon" [size]="12" class="shrink-0"></lucide-icon>
                        £500 Qualifier
                      </span>
                    }
                    <span [ngClass]="{
                      'px-2 py-0.5 text-xs font-medium rounded': true,
                      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300': ProductModelUtils.getCategoryColor(item.product.category) === 'primary',
                      'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300': ProductModelUtils.getCategoryColor(item.product.category) === 'info',
                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300': ProductModelUtils.getCategoryColor(item.product.category) === 'secondary',
                      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': ProductModelUtils.getCategoryColor(item.product.category) === 'success',
                      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300': ProductModelUtils.getCategoryColor(item.product.category) === 'warning',
                      'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300': ProductModelUtils.getCategoryColor(item.product.category) === 'light',
                      'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': ProductModelUtils.getCategoryColor(item.product.category) === 'error',
                      'bg-gray-800 text-gray-100 dark:bg-gray-600 dark:text-gray-200': ProductModelUtils.getCategoryColor(item.product.category) === 'dark'
                    }">
                      {{ item.product.category }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    Qty: {{ item.quantity !== null && item.quantity !== undefined ? item.quantity : '—' }}
                  </div>
                  @if (item.variant_selections && item.variant_selections.length > 0) {
                    <div class="mt-2 space-y-1">
                      @for (selection of item.variant_selections; track selection.id) {
                        <div class="text-xs text-gray-600 dark:text-gray-400 flex justify-between">
                          <span>{{ selection.variant?.name }}: {{ selection.variant_option?.name || 'Standard' }}</span>
                          <span>×{{ selection.quantity }}</span>
                        </div>
                      }
                    </div>
                  }
                  <div class="mt-2 text-lg font-bold text-gray-900 dark:text-white">£{{ item.calculated_price | number:'1.2-2' }}</div>
                  @if (isBlocked(item) && !isSelected(item)) {
                    <div class="mt-2 text-xs text-amber-700 dark:text-amber-300">
                      Required to maintain the £500 qualifying spend. Add an additional item if you want it discounted.
                    </div>
                  }
                </div>
              </label>
            </div>
          }
        </div>
      }
    </section>
  </div>

  <!-- Sticky Footer Actions -->
  <div class="sticky bottom-0 z-10 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6">
    <div class="flex gap-3">
      <button
        [ngClass]="{
          'flex-1 px-4 py-2 text-white rounded-lg transition-colors': true,
          'bg-green-600 hover:bg-green-700': hasChanges,
          'bg-gray-400 cursor-not-allowed': !hasChanges,
          'opacity-50 cursor-not-allowed': processing
        }"
        [disabled]="processing || !hasChanges"
        (click)="applyOffer()"
      >
        {{ processing ? 'Processing...' : 'Apply Offer' }}
      </button>
      <button
        class="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        [disabled]="processing"
        (click)="cancel()"
      >
        Cancel
      </button>
    </div>
  </div>
</div>
